import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CartItem, Product, BundleItemData } from '@/types/store';
import { trackCartEvent } from '@/hooks/useVisitorTracking';

interface CartStore {
  items: CartItem[];
  addItem: (product: Product & { nfcSong?: string; bundleSelections?: BundleItemData[] }, quantity?: number, size?: string, color?: string) => void;
  removeItem: (productId: string, size?: string, color?: string, bundleKey?: string) => void;
  updateQuantity: (productId: string, quantity: number, size?: string, color?: string, bundleKey?: string) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      
      addItem: (product, quantity = 1, size, color) => {
        trackCartEvent('add_to_cart', product.id, product.name, quantity);
        set((state) => {
          // Generate a unique key for bundle items based on selections
          const bundleKey = product.bundleSelections 
            ? JSON.stringify(product.bundleSelections.map(s => ({ id: s.bundleItemId, size: s.selectedSize, color: s.selectedColor })))
            : null;
          
          const existingItem = state.items.find((item) => {
            if (product.bundleSelections) {
              // For bundles, compare the bundleKey
              const itemBundleKey = item.bundleSelections 
                ? JSON.stringify(item.bundleSelections.map(s => ({ id: s.bundleItemId, size: s.selectedSize, color: s.selectedColor })))
                : null;
              return item.id === product.id && bundleKey === itemBundleKey;
            }
            // For regular products
            return item.id === product.id && item.selectedSize === size && item.selectedColor === color && item.nfcSong === product.nfcSong;
          });
          
          if (existingItem) {
            return {
              items: state.items.map((item) => {
                if (product.bundleSelections) {
                  const itemBundleKey = item.bundleSelections 
                    ? JSON.stringify(item.bundleSelections.map(s => ({ id: s.bundleItemId, size: s.selectedSize, color: s.selectedColor })))
                    : null;
                  if (item.id === product.id && bundleKey === itemBundleKey) {
                    return { ...item, quantity: item.quantity + quantity };
                  }
                } else if (item.id === product.id && item.selectedSize === size && item.selectedColor === color && item.nfcSong === product.nfcSong) {
                  return { ...item, quantity: item.quantity + quantity };
                }
                return item;
              }),
            };
          }
          
          return {
            items: [...state.items, { 
              ...product, 
              quantity, 
              selectedSize: size, 
              selectedColor: color, 
              nfcSong: product.nfcSong,
              bundleSelections: product.bundleSelections 
            }],
          };
        });
      },
      
      removeItem: (productId, size, color, bundleKey) => {
        trackCartEvent('remove_from_cart', productId);
        set((state) => ({
          items: state.items.filter((item) => {
            if (bundleKey) {
              const itemBundleKey = item.bundleSelections 
                ? JSON.stringify(item.bundleSelections.map(s => ({ id: s.bundleItemId, size: s.selectedSize, color: s.selectedColor })))
                : undefined;
              return !(item.id === productId && bundleKey === itemBundleKey);
            }
            return !(item.id === productId && item.selectedSize === size && item.selectedColor === color);
          }),
        }));
      },
      
      updateQuantity: (productId, quantity, size, color, bundleKey) => {
        set((state) => ({
          items: state.items.map((item) => {
            if (bundleKey) {
              const itemBundleKey = item.bundleSelections 
                ? JSON.stringify(item.bundleSelections.map(s => ({ id: s.bundleItemId, size: s.selectedSize, color: s.selectedColor })))
                : undefined;
              if (item.id === productId && bundleKey === itemBundleKey) {
                return { ...item, quantity: Math.max(0, quantity) };
              }
            } else if (item.id === productId && item.selectedSize === size && item.selectedColor === color) {
              return { ...item, quantity: Math.max(0, quantity) };
            }
            return item;
          }).filter((item) => item.quantity > 0),
        }));
      },
      
      clearCart: () => set({ items: [] }),
      
      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },
      
      getTotalPrice: () => {
        return get().items.reduce((total, item) => {
          const itemPrice = item.price + (item.nfcSong && item.nfcPrice ? item.nfcPrice : 0);
          return total + itemPrice * item.quantity;
        }, 0);
      },
    }),
    {
      name: 'cart-storage',
    }
  )
);
